import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { loadContractConstants, updateContractConstants } from '../../../store/participantService';
import { NO_CONTRACT_ERROR } from '../../../utils/formValidation/commonErrorConstants';
import { CONTRACT_CONSTANTS_ERROR } from '../../../utils/formValidation/loadingErrorMessageConstants';
import { hasRole, SUPERUSER } from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import RichTextEditor from '../../formElements/RichTextEditor';
import NotFound from '../../notFound/NotFound';
import LoadingSpinner from '../../ui/LoadingSpinner';

import { validate } from './validateDisclaimerAdmin';

import form from '../../../commonStyles/formStyles.module.css';

const acceptedRoles = [SUPERUSER];
const _MESSAGE = 'Disclaimer has been updated';

const DisclaimerAdmin = ({ contractId }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const [disclaimer, setDisclaimer] = useState('');
    const [errors, setErrors] = useState({});
    const [isDisabled, setIsDisabled] = useState(true);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const contractConstants = useSelector(
        (state) => state.entities.participantService.contractConstants
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USEEFFECTS
    useEffect(() => {
        if (!contractId || contractConstants?.contractId === contractId) return;
        dispatch(loadContractConstants(contractId));
    }, [contractId]);

    useEffect(() => {
        if (!roles.length) return;
        setIsDisabled(!hasRole(acceptedRoles, roles));
    }, [roles]);

    useEffect(() => {
        if (contractConstants?.contractId !== contractId) return;
        setDisclaimer(contractConstants.disclaimer ?? '');
    }, [contractConstants?.contractId]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === _MESSAGE) {
            setIsButtonDisabled(false);
        }
    }, [successMessage]);

    //  HELPER FNS
    const loadingError = () => {
        if (!contractId) return NO_CONTRACT_ERROR;
        if (Object.keys(contractConstants).length < 1) return CONTRACT_CONSTANTS_ERROR;
    };

    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { ...errors[key], error: false } }));
    };

    // EVENT HANDLERS
    const onUpdateDisclaimer = (content) => setDisclaimer(content);

    const onSubmit = (e) => {
        e.preventDefault();
        setIsButtonDisabled(true);
        const newContent =
            disclaimer === !disclaimer || disclaimer === '<p></p>' || disclaimer === '<p><br></p>'
                ? null
                : disclaimer.replaceAll('&nbsp;', ' ');
        const newErrors = validate(newContent, contractConstants.disclaimer);
        if (Object.keys(newErrors)?.length) {
            setErrors(newErrors);
            return;
        }
        dispatch(
            updateContractConstants(
                {
                    ...contractConstants,
                    disclaimer: newContent
                },
                _MESSAGE
            )
        );
    };

    // RENDER
    if (!hasRole(acceptedRoles, roles)) return <NotFound />;
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    return (
        <form
            onSubmit={onSubmit}
            className={form.formWrapper}
            data-testid="disclaimer_admin_form_start">
            <RichTextEditor
                id="disclaimerEditor"
                label="Disclaimer Content"
                placeholder="Enter disclaimer..."
                isCount={true}
                value={disclaimer}
                onChange={onUpdateDisclaimer}
            />

            <Button
                id="disclaimerAdmin"
                disabled={isButtonDisabled || isDisabled}
                content="UPDATE DISCLAIMER"
                error={errors.button}
                clearError={() => clearError('button')}
            />
        </form>
    );
};

DisclaimerAdmin.propTypes = {
    contractId: PropTypes.string
};

export default DisclaimerAdmin;
